import { FC, ReactNode, useState, useEffect } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import classnames from 'classnames';
import serverVars from 'server-vars';

import { defineMessages, FormattedMessage, useIntl } from 'dibs-react-intl';
import { Link } from 'dibs-elements/exports/Link';
import UploadIcon from 'dibs-icons/exports/legacy/ImageUploadMultiple';
import TrashCan from 'dibs-icons/exports/legacy/TrashCan';
import Plus from 'dibs-icons/exports/legacy/Plus';

import { MAX_IMAGE_MB, MAX_IMAGE_SIZE, MULTI_NUM_IMAGES } from '../../helpers/imageUploadValidator';

import styles from '../styles/ImageDropzone.scss';

import { ErrorCode } from './ImageUpload';

const ErrorMessage: FC<{ code: ErrorCode; maxFiles?: number }> = ({ code, maxFiles }) => {
    switch (code) {
        case 'file-too-large':
            return (
                <FormattedMessage
                    id="SellerApplicationForm.ImageUpload.error.tooLarge"
                    defaultMessage="The image is too large. Please upload an image less than {maxMb}MB."
                    values={{ maxMb: MAX_IMAGE_MB }}
                />
            );
        case 'too-many-files':
            return (
                <FormattedMessage
                    id="SellerApplicationForm.ImageUpload.error.tooMany"
                    defaultMessage="Please reduce the image count to {maxFiles}."
                    values={{ maxFiles }}
                />
            );
        case 'file-invalid-type':
            return (
                <FormattedMessage
                    id="SellerApplicationForm.ImageUpload.error.invalidType"
                    defaultMessage="This file format is not supported. Please use a JPG or JPEG."
                />
            );
        case 'invalid-dimensions':
            return (
                <FormattedMessage
                    id="SellerApplicationForm.ImageUpload.error.invalidDimensions"
                    defaultMessage="Invalid size. Height and width dimensions must be at least 768 pixels."
                />
            );
        default:
            return (
                <FormattedMessage
                    id="SellerApplicationForm.ImageUpload.error.somethingWrong"
                    defaultMessage="Something went wrong. Try again."
                />
            );
    }
};

const messages = defineMessages({
    imageAlt: {
        id: 'SellerApplicationForm.ImageUpload.imgAlt',
        defaultMessage: 'Uploaded image',
    },
});

export type Props = {
    multi: boolean;
    imageFiles: File[];
    fieldRequiredErrorMessage: ReactNode;
    errors: ErrorCode[];
    onDrop: (acceptedFiles: File[], fileRejections: FileRejection[]) => Promise<void>;
    onDelete: (imageFile: File) => void;
};

const ImageDropzone: FC<Props> = ({
    multi,
    imageFiles,
    fieldRequiredErrorMessage,
    errors,
    onDrop,
    onDelete,
}) => {
    const isMobile = serverVars.get('settings.isMobile');
    const intl = useIntl();
    const [imageUrls, setImageUrls] = useState(
        imageFiles.map(imageFile => URL.createObjectURL(imageFile))
    );
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        accept: ['image/jpeg'],
        multiple: multi,
        maxFiles: MULTI_NUM_IMAGES - imageFiles.length,
        maxSize: MAX_IMAGE_SIZE,
        onDrop,
    });
    const hasErrors = fieldRequiredErrorMessage || !!errors.length;

    useEffect(() => {
        setImageUrls(imageFiles.map(imageFile => URL.createObjectURL(imageFile)));
    }, [imageFiles]);

    const content = imageUrls.length ? (
        <div data-tn="image-dropzone-image" className={styles.imageWrapper}>
            <img
                className={styles.image}
                src={imageUrls[0]}
                alt={intl.formatMessage(messages.imageAlt)}
            />
        </div>
    ) : (
        <>
            <UploadIcon className={styles.uploadIcon} />
            <div data-tn="image-dropzone-upload">
                <FormattedMessage
                    id="SellerApplicationForm.ImageUpload.uploadImage2"
                    defaultMessage="{type, select,
                        desktop {<a>Upload an Image</a> or Drag Image Here}
                        other {<a>Upload an Image</a>}
                    }"
                    values={{
                        a: children => <Link onClick={() => {}}>{children}</Link>,
                        type: isMobile ? 'other' : 'desktop',
                    }}
                />
            </div>
        </>
    );

    return (
        <div className={styles.dropzoneWrapper}>
            {multi && imageUrls.length ? (
                <>
                    {imageUrls.length !== MULTI_NUM_IMAGES ? (
                        <div
                            className={classnames(styles.dropzone, {
                                [styles.error]: hasErrors,
                            })}
                        >
                            <div
                                className={classnames(styles.inputWrapper, {
                                    [styles.dragActive]: isDragActive,
                                })}
                                {...getRootProps()}
                            >
                                <input {...getInputProps()} data-tn="image-dropzone-input" />
                                <UploadIcon className={styles.uploadIcon} />
                                <div data-tn="image-dropzone-upload">
                                    <FormattedMessage
                                        id="SellerApplicationForm.ImageUpload.uploadImage"
                                        defaultMessage="{type, select,
                        desktop {<a>Upload Images</a> or Drag Images Here}
                        other {<a>Upload Images</a>}
                    }"
                                        values={{
                                            a: children => (
                                                <Link onClick={() => {}}>{children}</Link>
                                            ),
                                            type: isMobile ? 'other' : 'desktop',
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    ) : null}
                    <div className={styles.miniDropzoneWrapper}>
                        {[...Array(MULTI_NUM_IMAGES)].map((e, i) => {
                            let image: ReactNode;
                            const imageUrl = imageUrls?.[i];
                            const imageFile = imageFiles?.[i];
                            if (imageUrl) {
                                image = (
                                    <div className={styles.uploadedImageContainer}>
                                        <div className={styles.imageTrashContainer}>
                                            <img
                                                className={styles.image}
                                                src={imageUrl}
                                                alt={intl.formatMessage(messages.imageAlt)}
                                            />
                                            <div className={styles.trashOverlay}>
                                                <Link
                                                    className={styles.trashButton}
                                                    dataTn="edit-config-cta"
                                                    onClick={() => onDelete(imageFile)}
                                                >
                                                    <TrashCan className={styles.trashIcon} />
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                );
                            } else if (i < MULTI_NUM_IMAGES - 1) {
                                image = <Plus className={styles.plusIcon} />;
                            } else {
                                image = (
                                    <div className={styles.uploadUpToContainer}>
                                        <UploadIcon className={styles.largeUploadIcon} />
                                        <FormattedMessage
                                            id="SellerApplicationForm.ImageUpload.upToMaxImages"
                                            defaultMessage="Up to {maxImages} images"
                                            values={{ maxImages: MULTI_NUM_IMAGES }}
                                        />
                                    </div>
                                );
                            }
                            return (
                                <div
                                    data-tn="image-dropzone-image"
                                    className={styles.miniDropzoneDisplay}
                                    key={i}
                                >
                                    {image}
                                </div>
                            );
                        })}
                    </div>
                </>
            ) : (
                <div
                    className={classnames(styles.dropzone, {
                        [styles.error]: hasErrors,
                    })}
                >
                    <div
                        className={classnames(styles.inputWrapper, {
                            [styles.dragActive]: isDragActive,
                        })}
                        {...getRootProps()}
                    >
                        <input {...getInputProps()} data-tn="image-dropzone-input" />
                        {content}
                    </div>
                </div>
            )}
            {hasErrors && (
                <div className={styles.errors}>
                    {fieldRequiredErrorMessage && (
                        <div data-tn="image-dropzone-field-required">
                            {fieldRequiredErrorMessage}
                        </div>
                    )}
                    {!!errors.length &&
                        errors.map(code => (
                            <div key={code} data-tn={`image-dropzone-${code}`}>
                                <ErrorMessage
                                    code={code}
                                    maxFiles={multi ? MULTI_NUM_IMAGES - imageFiles.length : 1}
                                />
                            </div>
                        ))}
                </div>
            )}
        </div>
    );
};

export default ImageDropzone;
